import styled, { css } from "styled-components";

import { windowWidth } from "@styles/global";

/*
 * Same visual language as the announcements screen (organisms/
 * AnnouncementsDashboard): page padding of 24px 28px, a centred column of at
 * most 1240px, a 27px title over a 13px muted subtitle, white cards with a 12px
 * radius, list headers in 11px caps and rows with room to breathe.
 */

/**
 * MUI controls in the Identity look: sentence-case buttons in the brand navy
 * (MUI's default is an upper-case bright blue) and navy checkboxes. Applied to
 * the page and to the access dialog, which renders in a portal.
 */
const muiControls = css`
  .MuiButton-root {
    text-transform: none;
    letter-spacing: normal;
    font-size: 14px;
    font-weight: 600;
    border-radius: 8px;
    box-shadow: none;
  }

  .MuiButton-sizeSmall {
    font-size: 13px;
  }

  .MuiButton-containedPrimary {
    padding: 9px 20px;
    background: ${(props) => props.theme.colors.primary};

    &:hover {
      background: ${(props) => props.theme.brand.navyHover};
      box-shadow: none;
    }

    &.Mui-disabled {
      background: ${(props) => props.theme.brand.line};
      color: ${(props) => props.theme.brand.soft};
    }
  }

  .MuiButton-outlinedPrimary {
    padding: 8px 18px;
    border-color: ${(props) => props.theme.brand.line};
    color: ${(props) => props.theme.brand.navy};

    &:hover {
      border-color: #c0cfe0;
      background: #f5f8fb;
    }
  }

  .MuiButton-textPrimary {
    color: ${(props) => props.theme.brand.navyMed};
  }

  .MuiCheckbox-root.Mui-checked,
  .MuiCheckbox-root.MuiCheckbox-indeterminate {
    color: ${(props) => props.theme.brand.navy};
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.brand.navyMed};
  }

  .MuiInputLabel-root.Mui-focused {
    color: ${(props) => props.theme.brand.navyMed};
  }
`;

/* region Page */

/**
 * The page scrolls on its own: the Office layout clips what overflows it.
 * Unpadded on purpose — Chrome insets sticky elements by the scroll
 * container's padding, which would let rows show past the review's sticky
 * header and footer. The padding lives on Content instead.
 */
export const Wrapper = styled.div`
  height: 100%;
  overflow-y: auto;

  ${muiControls}
`;

/**
 * Up to 1600px of content plus its 28px sides — wider than the announcements
 * screen's 1240px, because the review is a table of hundreds of users with
 * four columns of decisions, and a laptop screen should be all list.
 */
export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1656px;
  margin: 0 auto;
  padding: 24px 28px;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const PageTitle = styled.div`
  padding-top: 5px;
  font-size: 27px;
  color: ${(props) => props.theme.brand.ink};

  @media ( max-width: ${windowWidth.laptop.large} ) {
    font-size: 23px;
  }
`;

export const PageSubtitle = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.brand.muted};
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.brand.line};
  color: ${(props) => props.theme.brand.navy};
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #c0cfe0;
    background: #f5f8fb;
  }
`;

export const Banner = styled.div<{ $tone?: "error" | "warning" | "info" | "success" }>`
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;

  ${({ $tone }) => {
    if ($tone === "error") return css`background: rgba(231, 76, 60, 0.1); color: #b03a2e;`;
    if ($tone === "warning") return css`background: rgba(255, 173, 0, 0.14); color: #8a5f00;`;
    if ($tone === "success") return css`background: rgba(70, 193, 125, 0.14); color: #257d4f;`;

    return css`background: rgba(41, 104, 168, 0.1); color: #1f5287;`;
  }}
`;

/**
 * One step of the assistant. `clip` rounds off the review's sticky footer at
 * the end of the list without becoming a scroll container, which would stop
 * that footer and the table header from sticking.
 */
export const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 53, 99, 0.06);
  padding: 24px 28px;
  overflow: clip;
`;

export const StepTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.brand.ink};
`;

export const StepHint = styled.p`
  margin: 0 0 20px;
  max-width: 90ch;
  font-size: 13px;
  line-height: 1.6;
  color: ${(props) => props.theme.brand.muted};
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const Spacer = styled.span`
  flex: 1;
`;

/** Same width as the search on the announcements screen. */
export const SearchBox = styled.div`
  width: 280px;
  max-width: 100%;
`;

/**
 * Toolbar actions that are the exception, not the main path. Pushed right by
 * their own margin, so that when the toolbar wraps they stay on the right of
 * the new line instead of starting it.
 */
export const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
  width: 100%;
`;

export const Empty = styled.p`
  margin: 32px 0;
  text-align: center;
  font-size: 13px;
  color: ${(props) => props.theme.brand.muted};
`;

/* endregion Page */

/* region Stepper */

export const Steps = styled.ol`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Step = styled.li<{ $state: "done" | "current" | "next" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 8px;
  border: 1px solid transparent;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
  }

  ${({ $state, theme }) => {
    if ($state === "done") {
      return css`
        background: rgba(70, 193, 125, 0.14);
        color: #2e9c62;
        span { background: #2e9c62; color: #ffffff; }
      `;
    }

    if ($state === "current") {
      return css`
        background: ${theme.brand.navy};
        color: #ffffff;
        span { background: #ffffff; color: ${theme.brand.navy}; }
      `;
    }

    return css`
      background: #ffffff;
      border-color: #e8edf3;
      color: ${theme.brand.soft};
      span { background: #eef1f5; color: ${theme.brand.soft}; }
    `;
  }}
`;

/* endregion Stepper */

/** Status chip, as on the announcements list. */
export const Badge = styled.span<{ $tone?: "ok" | "info" | "bad" | "muted" | "new" }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;

  ${({ $tone }) => {
    if ($tone === "ok") return css`background: rgba(70, 193, 125, 0.14); color: #2e9c62;`;
    if ($tone === "info") return css`background: rgba(41, 104, 168, 0.1); color: #2968a8;`;
    if ($tone === "bad") return css`background: rgba(231, 76, 60, 0.12); color: #c0392b;`;
    if ($tone === "new") return css`background: rgba(255, 173, 0, 0.16); color: #b97f00;`;

    return css`background: #eef1f5; color: #8a9ab0;`;
  }}
`;

/* region Customer list */

export const TenantList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 60vh;
  overflow: auto;
  border: 1px solid #e8edf3;
  border-radius: 10px;
`;

export const TenantRow = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f3f7;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafbfd;
  }

  /* The name takes the slack so every row's button lines up on the right. */
  > div:first-child {
    flex: 1;
  }
`;

export const TenantName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  strong {
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.brand.ink};
  }

  span {
    font-size: 12px;
    color: ${(props) => props.theme.brand.soft};
  }
`;

/* endregion Customer list */

/* region Review table */

/**
 * The list grows with the page: one scroll, not a box scrolling inside a page
 * that scrolls too. `clip` rounds the corners without becoming a scroll
 * container, so the table header still sticks to the top of the page.
 */
export const TableScroll = styled.div`
  border: 1px solid #e8edf3;
  border-radius: 10px;
  overflow: clip;
`;

/** Quick filters over the review list, each with its count. */
export const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const FilterChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.brand.navy : theme.brand.line)};
  border-radius: 20px;
  background: ${({ $active, theme }) => ($active ? theme.brand.navy : "#ffffff")};
  color: ${({ $active, theme }) => ($active ? "#ffffff" : theme.brand.ink)};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  span {
    font-weight: 500;
    color: ${({ $active, theme }) => ($active ? "rgba(255, 255, 255, 0.75)" : theme.brand.soft)};
  }

  &:hover {
    border-color: ${({ $active, theme }) => ($active ? theme.brand.navy : "#c0cfe0")};
  }
`;

/**
 * The review's actions stay at the bottom of the screen while the list
 * scrolls, with the count the operator is about to confirm.
 */
export const StickyActions = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 24px -28px -24px;
  padding: 14px 28px;
  background: #ffffff;
  border-top: 1px solid #eef1f5;
  box-shadow: 0 -6px 12px -10px rgba(0, 53, 99, 0.25);
`;

export const Table = styled.table<{ $selectable?: boolean }>`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f3f7;
    font-size: 13px;
    color: #3a4a5c;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: #fafbfd;
  }

  /* MUI pads its checkbox for touch targets, which would double every row. */
  .MuiCheckbox-root {
    padding: 4px;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding-top: 13px;
    padding-bottom: 13px;
    background: #fafbfd;
    border-bottom: 1px solid #eef1f5;

    /* A collapsed border stays behind when the header sticks; the shadow
       goes with it, so the rows still slide under a line. */
    box-shadow: inset 0 -1px 0 #eef1f5;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(props) => props.theme.brand.soft};
  }

  /* The select column only holds a checkbox. The decision columns get fixed
     shares — password, modules, legacy — so a chip taken off or put back
     does not make the columns jump, and extra width goes to the user. */
  ${({ $selectable }) => $selectable && css`
    th:first-child,
    td:first-child {
      width: 1%;
      padding-right: 0;
    }

    th:nth-child(3) {
      width: 16%;
    }

    th:nth-child(4) {
      width: 22%;
    }

    th:nth-child(5) {
      width: 20%;
    }
  `}
`;

/** Under the list: which users are on screen, and the pages. */
export const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
  font-size: 13px;
  color: ${(props) => props.theme.brand.muted};

  .MuiPaginationItem-root {
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.brand.ink};
    border-radius: 8px;
  }

  .MuiPaginationItem-root.Mui-selected,
  .MuiPaginationItem-root.Mui-selected:hover {
    background: ${(props) => props.theme.brand.navy};
    color: #ffffff;
  }
`;

/** Name over e-mail, like the title and preview of an announcement row. */
export const UserCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const UserName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.brand.ink};
`;

export const UserEmail = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.brand.soft};
  overflow-wrap: anywhere;
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;

  /* The chips carry each product's own colour, so the delete affordance has to
     be light to stay visible on top of it. Scoped under .MuiChip-root to
     outrank MUI's own rule, which has the same weight and is injected later. */
  .MuiChip-root .MuiChip-deleteIcon {
    color: rgba(255, 255, 255, 0.75);

    &:hover {
      color: #ffffff;
    }
  }
`;

/** Header of the "Legado" column: the toggle-all checkbox and the title. */
export const LegacyHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

/** The per-user lock: checkbox with its label, and the other legacy areas under it. */
export const LegacyCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  .MuiFormControlLabel-root {
    margin: 0;
    gap: 2px;
  }

  .MuiFormControlLabel-label {
    font-size: 13px;
    color: #3a4a5c;
  }
`;

export const LegacyAreas = styled.span`
  padding-left: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #b97f00;
  white-space: nowrap;
`;

/** Users that cannot be migrated: present, but out of the decision. */
export const Ineligible = styled.div`
  margin-bottom: 16px;

  .MuiAccordion-root {
    border: 1px solid #e8edf3;
    border-radius: 10px !important;
    box-shadow: none;
    overflow: hidden;

    &::before {
      display: none;
    }
  }

  .MuiAccordionSummary-root {
    padding: 0 20px;
  }

  .MuiAccordionSummary-content {
    font-size: 13px;
    font-weight: 600;
    color: #8a5f00;
  }

  .MuiAccordionDetails-root {
    padding: 0;
    border-top: 1px solid #eef1f5;
  }
`;

/** A customer can have dozens of these: the list opens with a height of its own. */
export const IneligibleScroll = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

/* endregion Review table */

/* region Progress and result */

/** Steps the operator only watches: everything centred on the one message. */
export const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  padding: 24px 0 8px;

  ${StepTitle},
  ${StepHint} {
    margin: 0;
  }
`;

export const Progress = styled.div<{ $percent: number }>`
  width: min(520px, 100%);
  height: 10px;
  border-radius: 6px;
  background: #e3e8ef;
  overflow: hidden;

  div {
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    background: ${(props) => props.theme.brand.navy};
    transition: width 0.4s ease;
  }
`;

export const ProgressLabel = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme.brand.ink};
`;

/** The big tick (or bang) that opens the last step. */
export const Outcome = styled.div<{ $tone: "ok" | "bad" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 1.8rem;
  font-weight: 700;
  color: #ffffff;
  background: ${({ $tone }) => ($tone === "bad" ? "#c0392b" : "#2e9c62")};
`;

export const Stats = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin: 6px 0;
`;

export const Stat = styled.div<{ $tone?: "ok" | "info" | "bad" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 112px;
  padding: 14px 18px;
  border: 1px solid #e8edf3;
  border-radius: 10px;
  background: #fafbfd;

  b {
    font-size: 22px;
    font-weight: 600;
    color: ${(props) => props.theme.brand.ink};

    ${({ $tone }) => {
    if ($tone === "ok") return css`color: #2e9c62;`;
    if ($tone === "info") return css`color: #2968a8;`;
    if ($tone === "bad") return css`color: #c0392b;`;

    return css``;
  }}
  }

  span {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(props) => props.theme.brand.soft};
  }
`;

/* endregion Progress and result */

/* region Access dialog — same layout as the announcement managers dialog */

export const AccessContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 560px;
  max-width: 100%;
  max-height: 60vh;
  overflow-y: auto;

  ${muiControls}

  ${Banner} {
    margin-bottom: 8px;
  }
`;

export const GrantRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid #eef1f5;

  > :first-child {
    flex-grow: 1;
  }

  button {
    white-space: nowrap;
  }
`;

export const ManagerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f3f7;

  .MuiIconButton-root {
    color: ${(props) => props.theme.brand.navy};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const ManagerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ManagerName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.brand.ink};
`;

export const ManagerEmail = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.brand.soft};
`;

export const GrantedBy = styled.span`
  font-size: 11px;
  font-style: italic;
  color: ${(props) => props.theme.brand.soft};
`;

/* endregion Access dialog */

/* region TopconFLEET source */

/** "?" next to a word that needs explaining; sized to sit inline with 11–13px text. */
export const HelpTipAnchor = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  .MuiIconButton-root {
    padding: 2px;
    font-size: 16px;
    color: ${(props) => props.theme.brand.soft};
  }

  .MuiIconButton-root:hover,
  .MuiIconButton-root[aria-expanded="true"] {
    color: ${(props) => props.theme.brand.navy};
  }
`;

export const HelpTipText = styled.span`
  display: block;
  max-width: 360px;
  max-height: 320px;
  overflow-y: auto;
  overflow-wrap: anywhere;
  font-size: 12px;
  line-height: 1.5;
`;

/** Where the users come from, above the customer question. */
export const SourcePicker = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid #eef1f5;
`;

export const SourceLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(props) => props.theme.brand.soft};
`;

/** A label and its "?" kept on one line. */
export const WithHelp = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
`;

/** The one rule of the review that cannot be undone: set apart from the hint, with its own icon. */
export const Callout = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: -6px 0 20px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 173, 0, 0.45);
  border-left: 4px solid #e0a100;
  border-radius: 10px;
  background: rgba(255, 173, 0, 0.08);
  font-size: 13px;
  line-height: 1.55;
  color: #6b4b00;

  svg {
    flex: none;
    margin-top: 1px;
    font-size: 20px;
    color: #b97f00;
  }

  strong {
    display: block;
    margin-bottom: 2px;
    color: #4d3600;
  }
`;

/** The confirmation dialog: one line per consequence, each led by its icon — no boxes. */
export const ConfirmList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
`;

/** The irreversible line stands out by its colour alone. */
export const ConfirmItem = styled.li<{ $tone?: "warning" }>`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ $tone }) => ($tone === "warning" ? "#8a5f00" : "#3a4a5c")};
  font-weight: ${({ $tone }) => ($tone === "warning" ? 500 : 400)};

  svg {
    flex: none;
    margin-top: 1px;
    font-size: 20px;
    color: ${({ $tone, theme }) => ($tone === "warning" ? "#b97f00" : theme.brand.navy)};
  }
`;

export const ConfirmNote = styled.p`
  margin: 14px 0 0;
  font-size: 12px;
  color: ${(props) => props.theme.brand.soft};
`;

/** The per-user report under the totals of the last step. */
export const ResultReport = styled.div`
  width: 100%;
  margin-top: 8px;
  text-align: left;
`;

export const ResultReportHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.brand.ink};
  }
`;

export const ResultScroll = styled.div`
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e8edf3;
  border-radius: 10px;
`;

/** The reason on one line; a longer one is cut and read whole in its "?". */
export const ResultDetail = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  max-width: 460px;
  color: #5b6b7d;
`;

export const ResultDetailText = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/* endregion TopconFLEET source */
